import { useCallback, useEffect, useRef } from "react";
import { getBrowserApiBase } from "../lib/browser-api-base";
import { getAccessToken } from "../lib/supabase";

const LAST_SYNC_KEY = "fintheon:x-bookmarks:auto-sync:last";
const SYNC_INTERVAL_MS = 10 * 60 * 1000;
const SYNC_COOLDOWN_MS = 5 * 60 * 1000;
const SYNC_LIMIT = 60;
const AGENTIC_DESK_FOLDER = "Agentic Desk";

interface XConnectionStatus {
  connected?: boolean;
}

interface XBookmarkSyncResponse {
  ok?: boolean;
  imported?: number;
  error?: string;
}

export function XBookmarksAutoSyncBridge() {
  const syncingRef = useRef(false);

  const syncBookmarks = useCallback(async () => {
    if (syncingRef.current || wasSyncedRecently()) return;

    syncingRef.current = true;
    try {
      const token = await getAccessToken();
      if (!token) return;
      const isXConnected = await hasConnectedXIntegration(token);
      if (!isXConnected) return;

      const result = await syncXBookmarks(token);
      writeLastSync();
      if ((result.imported ?? 0) > 0) openBookmarksTab(result.imported ?? 0);
    } catch (error) {
      writeLastSync();
      console.warn("[XBookmarksAutoSync] sync skipped", error);
    } finally {
      syncingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) void syncBookmarks();
    };

    run();
    const interval = window.setInterval(run, SYNC_INTERVAL_MS);
    window.addEventListener("focus", run);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", run);
    };
  }, [syncBookmarks]);

  return null;
}

async function hasConnectedXIntegration(token: string): Promise<boolean> {
  const res = await fetch(`${getBrowserApiBase()}/api/integrations/x/status`, {
    credentials: "include",
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return false;

  const status = (await res
    .json()
    .catch(() => null)) as XConnectionStatus | null;
  return status?.connected === true;
}

async function syncXBookmarks(token: string): Promise<XBookmarkSyncResponse> {
  const res = await fetch(`${getBrowserApiBase()}/api/integrations/x/sync`, {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      limit: SYNC_LIMIT,
      folderName: AGENTIC_DESK_FOLDER,
    }),
    signal: AbortSignal.timeout(30_000),
  });
  const data = (await res.json().catch(() => ({}))) as XBookmarkSyncResponse;
  if (!res.ok) throw new Error(data.error || "X bookmark sync failed");
  return data;
}

function openBookmarksTab(imported: number) {
  window.dispatchEvent(
    new CustomEvent("fintheon:desk-rail-open-tab", {
      detail: { tab: "bookmarks", source: "x-bookmarks", imported },
    }),
  );
  window.setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("fintheon:bookmarks-refresh", {
        detail: { sourceType: "x_bookmark", imported },
      }),
    );
  }, 120);
}

function wasSyncedRecently(): boolean {
  try {
    const last = Number(localStorage.getItem(LAST_SYNC_KEY) ?? 0);
    return Number.isFinite(last) && Date.now() - last < SYNC_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function writeLastSync() {
  try {
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
  } catch {
    /* ignore private storage failures */
  }
}
